import { useState } from 'react';
import { useReports } from '../hooks/useReports';
import { usePatients } from '../hooks/usePatients';
import { useProfessionals } from '../hooks/useProfessionals';
import { useAppointments } from '../hooks/useAppointments';
import { useMedicalRecords } from '../hooks/useMedicalRecords';
import { 
  AtendimentosReportData,
  PacientesReportData,
  ProfissionaisReportData,
  IndicadoresReportData,
  ReportData
} from '../hooks/useReports';
import { Patient, Appointment, UserProfile } from '../types';
import { 
  FileText, 
  Download, 
  Calendar, 
  Users, 
  Stethoscope, 
  BarChart3,
  TrendingUp,
  Activity
} from 'lucide-react';

export default function Reports() {
  const { generateReport, loading } = useReports();
  const { patients } = usePatients();
  const { professionals } = useProfessionals();
  const { appointments } = useAppointments();
  const { medicalRecords } = useMedicalRecords();
  
  const [reportType, setReportType] = useState<ReportData['type']>('atendimentos');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    professionalId: '',
    specialty: '',
    status: ''
  });

  const handleGenerateReport = async () => {
    if (reportType === 'atendimentos') {
      const reportData: AtendimentosReportData = {
        type: 'atendimentos',
        dateRange,
        filters,
        data: getReportData() as Appointment[]
      };
      await generateReport(reportData);
    } else if (reportType === 'pacientes') {
      const reportData: PacientesReportData = {
        type: 'pacientes',
        dateRange,
        filters,
        data: getReportData() as Patient[]
      };
      await generateReport(reportData);
    } else if (reportType === 'profissionais') {
      const reportData: ProfissionaisReportData = {
        type: 'profissionais',
        dateRange,
        filters,
        data: getReportData() as UserProfile[]
      };
      await generateReport(reportData);
    } else if (reportType === 'indicadores') {
      const reportData: IndicadoresReportData = {
        type: 'indicadores',
        dateRange,
        filters,
        data: getReportData() as IndicadoresReportData['data']
      };
      await generateReport(reportData);
    }
  };

  const getReportData = () => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    switch (reportType) {
      case 'atendimentos':
        return appointments.filter(appointment => {
          const appointmentDate = new Date(appointment.date);
          return appointmentDate >= startDate && appointmentDate <= endDate;
        });
      case 'pacientes':
        return patients.filter(patient => {
          const createdDate = new Date(patient.createdAt);
          return createdDate >= startDate && createdDate <= endDate;
        });
      case 'profissionais':
        return professionals.filter(professional => {
          const createdDate = new Date(professional.createdAt);
          return createdDate >= startDate && createdDate <= endDate;
        });
      case 'indicadores':
        return {
          totalPatients: patients.length,
          totalProfessionals: professionals.length,
          totalAppointments: appointments.length,
          totalMedicalRecords: medicalRecords.length,
          appointmentsByStatus: appointments.reduce((acc, appointment) => {
            acc[appointment.status] = (acc[appointment.status] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        };
      default:
        return [];
    }
  };

  const getQuickStats = () => {
    const today = new Date();
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const appointmentsThisMonth = appointments.filter(appointment => 
      new Date(appointment.date) >= thisMonth
    ).length;
    
    const newPatientsThisMonth = patients.filter(patient => 
      new Date(patient.createdAt) >= thisMonth
    ).length;
    
    const recordsThisMonth = medicalRecords.filter(record => 
      new Date(record.createdAt) >= thisMonth
    ).length;
    
    return {
      appointmentsThisMonth,
      newPatientsThisMonth,
      recordsThisMonth,
      totalPatients: patients.length
    };
  };

  const stats = getQuickStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">Gere relatórios e análises do sistema</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-blue-500 p-3 rounded-lg">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Consultas este mês</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.appointmentsThisMonth}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-green-500 p-3 rounded-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Novos pacientes</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.newPatientsThisMonth}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-purple-500 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Prontuários criados</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.recordsThisMonth}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-orange-500 p-3 rounded-lg">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de pacientes</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalPatients}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Generator */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Gerador de Relatórios
          </h3>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Relatório
            </label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
               { value: 'atendimentos', label: 'Atendimentos', icon: Calendar },
               { value: 'pacientes', label: 'Pacientes', icon: Users },
               { value: 'profissionais', label: 'Profissionais', icon: Stethoscope },
               { value: 'indicadores', label: 'Indicadores', icon: TrendingUp }
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                 onClick={() => setReportType(value as any)}
                  className={`p-4 border-2 rounded-lg flex flex-col items-center space-y-2 transition-colors ${
                    reportType === value
                      ? 'border-medical-500 bg-medical-50 text-medical-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Inicial
              </label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Final
              </label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
              />
            </div>
          </div>

          {/* Filters */}
          {reportType === 'atendimentos' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profissional
                </label>
                <select
                  value={filters.professionalId}
                  onChange={(e) => setFilters(prev => ({ ...prev, professionalId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                >
                  <option value="">Todos os profissionais</option>
                  {professionals.map(professional => (
                    <option key={professional.id} value={professional.id}>
                      {professional.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Especialidade
                </label>
                <input
                  type="text"
                  value={filters.specialty}
                  onChange={(e) => setFilters(prev => ({ ...prev, specialty: e.target.value }))}
                  placeholder="Filtrar por especialidade"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                >
                  <option value="">Todos os status</option>
                  <option value="agendado">Agendado</option>
                  <option value="confirmado">Confirmado</option>
                  <option value="concluido">Concluído</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-medical-600 text-white px-6 py-2 rounded-lg hover:bg-medical-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{loading ? 'Gerando...' : 'Gerar Relatório'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Report Preview */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Prévia do Relatório</h3>
        </div>
        
        <div className="p-6">
          <div className="text-center py-12 text-gray-500">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>Selecione os parâmetros e clique em "Gerar Relatório" para visualizar os dados</p>
          </div>
        </div>
      </div>
    </div>
  );
}